// ════════════════════════════════════════════════════════════════════════════
// LpaTextarea — textarea primitivo com auto-resize opcional
// ════════════════════════════════════════════════════════════════════════════

import React, { useEffect, useRef } from "react";

export interface LpaTextareaProps extends Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  "onChange" | "rows"
> {
  rows?: number;
  invalid?: boolean;
  fullWidth?: boolean;
  /** Cresce automaticamente conforme conteúdo */
  autoResize?: boolean;
  onChange?: (value: string) => void;
  testId?: string;
}

const LpaTextarea = React.forwardRef<HTMLTextAreaElement, LpaTextareaProps>(function LpaTextarea(
  {
    rows = 3,
    invalid = false,
    fullWidth = true,
    autoResize = false,
    onChange,
    value,
    style,
    className,
    testId,
    ...rest
  },
  ref
) {
  const innerRef = useRef<HTMLTextAreaElement | null>(null);
  const setRef = (node: HTMLTextAreaElement | null) => {
    innerRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
  };

  useEffect(() => {
    if (!autoResize || !innerRef.current) return;
    const el = innerRef.current;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value, autoResize]);

  return (
    <textarea
      ref={setRef}
      rows={rows}
      value={value}
      onChange={(e) => onChange?.(e.target.value)}
      data-testid={testId}
      aria-invalid={invalid || undefined}
      className={className}
      style={{
        width: fullWidth ? "100%" : undefined,
        padding: "10px 12px",
        fontSize: "var(--lpa-text-lg)",
        fontFamily: "var(--lpa-font-sans)",
        color: "var(--lpa-text)",
        background: "var(--lpa-bg)",
        border: `1px solid ${invalid ? "var(--lpa-danger)" : "var(--lpa-border)"}`,
        borderRadius: "var(--lpa-radius-md)",
        outline: "none",
        resize: autoResize ? "none" : "vertical",
        lineHeight: 1.5,
        minHeight: 80,
        transition: "border-color var(--lpa-tr-fast), box-shadow var(--lpa-tr-fast)",
        ...style,
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = "var(--lpa-accent)";
        e.currentTarget.style.boxShadow = "0 0 0 3px var(--lpa-accent-soft)";
        rest.onFocus?.(e);
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = invalid ? "var(--lpa-danger)" : "var(--lpa-border)";
        e.currentTarget.style.boxShadow = "none";
        rest.onBlur?.(e);
      }}
      {...rest}
    />
  );
});

export default LpaTextarea;
